import { pipe } from "fp-ts/function"
import * as E from "fp-ts/Either"
import * as A from "fp-ts/Array"
import * as Ord from "fp-ts/Ord"
import * as N from "fp-ts/number"
import * as TE from "fp-ts/TaskEither"
import * as O from "fp-ts/Option"
import { map } from "rxjs/operators"

import {
  download,
  load,
  close,
  clear,
  remove,
  type DownloadResponse,
  type LoadOptions,
  type LoadResponse,
  type CloseResponse,
  type RemoveResponse,
} from "@hoppscotch/plugin-appload"

import { Service } from "dioc"
import { BehaviorSubject, Observable } from "rxjs"
import { getCurrentWebviewWindow } from "@tauri-apps/api/webviewWindow"

import {
  Instance,
  ConnectionState,
  OperationResult,
  InstanceKind,
  InstancePlatformDef,
  VENDORED_INSTANCE_CONFIG,
} from "@hoppscotch/common/platform/instance"

import { Store } from "@app/kernel/store"
import { getKernelMode } from "@hoppscotch/kernel"
import { diag } from "@hoppscotch/common/kernel/log"
import { getService } from "@hoppscotch/common/modules/dioc"

const STORE_NAMESPACE = "hoppscotch-desktop.v1"

type RecentInstances = Instance[]

const sortByLastUsedDesc = A.sort(
  pipe(
    N.Ord,
    Ord.reverse,
    Ord.contramap((instance: Instance) => new Date(instance.lastUsed).getTime())
  )
)

export class DesktopInstanceService
  extends Service<ConnectionState>
  implements InstancePlatformDef
{
  public static readonly ID = "DESKTOP_INSTANCE_SERVICE"

  private connectionState$ = new BehaviorSubject<ConnectionState>(
    this.getDefaultConnectionState()
  )
  private recentInstances$ = new BehaviorSubject<RecentInstances>(
    this.getDefaultRecentInstances()
  )

  // Only enabled when kernel mode is desktop, not web.
  public readonly instanceSwitchingEnabled: boolean =
    getKernelMode() === "desktop"

  // Configuration for instance management behavior. maxRecentInstances limits how many
  // instances get persisted to avoid unbounded growth. connectionTimeout prevents
  // hanging on unresponsive servers. confirmDestructiveOperations gates dangerous
  // actions behind user confirmation.
  public readonly config: InstancePlatformDef["config"] = {
    maxRecentInstances: 10,
    defaultWindowOptions: {
      title: "Hoppscotch",
      width: 1200,
      height: 800,
      resizable: true,
    },
    autoReconnect: false,
    connectionTimeout: 30000,
    confirmDestructiveOperations: true,
  }

  /**
   * Gets observable stream of connection state
   */
  public getConnectionStateStream(): Observable<ConnectionState> {
    return this.connectionState$.asObservable()
  }

  /**
   * Gets observable stream of recent instances
   */
  public getRecentInstancesStream(): Observable<RecentInstances> {
    return this.recentInstances$.asObservable()
  }

  /**
   * Gets observable stream of the current active instance
   */
  public getCurrentInstanceStream(): Observable<Instance | null> {
    return this.getConnectionStateStream().pipe(
      map((state) => (state.status === "connected" ? state.instance : null))
    )
  }

  /**
   * Gets current connection state synchronously
   */
  public getCurrentConnectionState(): ConnectionState {
    return this.connectionState$.value
  }

  /**
   * Gets recent instances synchronously
   */
  public getRecentInstances(): RecentInstances {
    return this.recentInstances$.value
  }

  /**
   * Gets the current active instance synchronously
   */
  public getCurrentInstance(): Instance | null {
    const state = this.getCurrentConnectionState()
    return state.status === "connected" ? state.instance : null
  }

  /**
   * Connects to an instance
   */
  public async connectToInstance(
    serverUrl: string,
    instanceKind: InstanceKind,
    displayName?: string,
    options?: Partial<LoadOptions>
  ): Promise<OperationResult> {
    diag(
      "instance",
      `connectToInstance: serverUrl=${serverUrl}, kind=${instanceKind}`
    )

    if (this.beforeConnect) {
      const shouldContinue = await this.beforeConnect(
        serverUrl,
        instanceKind,
        displayName
      )
      if (!shouldContinue) {
        diag("instance", "connectToInstance: cancelled by beforeConnect hook")
        return {
          success: false,
          message: "Connection cancelled by beforeConnect hook",
        }
      }
    }

    // snapshot so we can restore on failure. connectToInstanceTE may or may
    // not set "connecting" depending on whether the instance already exists
    // (existing instances skip downloadInstanceTE which is where
    // setConnectingStateTE lives). only restore when the current state is
    // actually "connecting" to avoid clobbering a valid "connected" state,
    // e.g. if updateInstanceStateTE already set "connected" but a later
    // chainFirst step (updateInstanceLastUsed, performCloseTE) failed
    const prevState = this.connectionState$.value

    const result = await this.connectToInstanceTE(
      serverUrl,
      instanceKind,
      displayName,
      options
    )()

    const operationResult = pipe(
      result,
      E.fold(
        (error): OperationResult => {
          diag("instance", `connectToInstance failed: ${error}`)
          if (this.connectionState$.value.status === "connecting") {
            this.restoreConnectionState(prevState)
          }
          if (this.onConnectionError) {
            this.onConnectionError(error, serverUrl)
          }
          return { success: false, message: error }
        },
        (operationResult) => {
          diag("instance", "connectToInstance successful")
          return operationResult
        }
      )
    )

    if (operationResult.success && this.afterConnect) {
      const currentInstance = this.getCurrentInstance()
      if (currentInstance) {
        await this.afterConnect(currentInstance)
      }
    }

    return operationResult
  }

  /**
   * Downloads an instance without connecting
   */
  public async downloadInstance(
    serverUrl: string,
    instanceKind: InstanceKind,
    displayName?: string
  ): Promise<OperationResult> {
    diag("instance", `downloadInstance: serverUrl=${serverUrl}`)

    // snapshot before downloadInstanceTE sets "connecting" via
    // setConnectingStateTE, so we can restore on failure instead of
    // clobbering a valid "connected" state
    const prevState = this.connectionState$.value

    const result = await this.downloadInstanceTE(
      serverUrl,
      instanceKind,
      displayName
    )()

    return pipe(
      result,
      E.fold(
        (error): OperationResult => {
          diag("instance", `downloadInstance failed: ${error}`)
          if (this.connectionState$.value.status === "connecting") {
            this.restoreConnectionState(prevState)
          }
          if (this.onDownloadError) {
            this.onDownloadError(error, serverUrl)
          }
          return { success: false, message: error }
        },
        (instance): OperationResult => {
          diag(
            "instance",
            `downloadInstance successful: ${instance.displayName}`
          )
          // downloadInstanceTE sets "connecting" via setConnectingStateTE but
          // the success path here is download-only (no loadInstanceTE follows),
          // so nothing resets the state to "connected". restore prevState to
          // avoid leaving the UI stuck in "connecting" after a successful
          // download
          if (this.connectionState$.value.status === "connecting") {
            this.restoreConnectionState(prevState)
          }
          return {
            success: true,
            message: `Successfully downloaded ${instance.displayName}`,
            data: instance,
          }
        }
      )
    )
  }

  /**
   * Loads a previously downloaded instance
   */
  public async loadInstance(
    instance: Instance,
    options?: Partial<LoadOptions>
  ): Promise<OperationResult> {
    diag(
      "instance",
      `loadInstance called: kind=${instance.kind}, displayName=${instance.displayName}, host=${options?.host ?? "(none)"}`
    )

    // loadInstanceTE doesn't call setConnectingStateTE itself, so the
    // connection state isn't necessarily "connecting" when this runs.
    // snapshot and restore to avoid clobbering a valid "connected" state
    const prevState = this.connectionState$.value

    const result = await this.loadInstanceTE(instance, options)()

    return pipe(
      result,
      E.fold(
        (error): OperationResult => {
          diag("instance", `loadInstance failed: ${error}`)
          if (this.connectionState$.value.status === "connecting") {
            this.restoreConnectionState(prevState)
          }
          if (this.onLoadError) {
            this.onLoadError(error, instance)
          }
          return { success: false, message: error }
        },
        (loadResponse): OperationResult => {
          diag("instance", `loadInstance successful: ${instance.displayName}`)
          return {
            success: true,
            message: `Successfully loaded ${instance.displayName}`,
            data: loadResponse,
          }
        }
      )
    )
  }

  /**
   * Removes an instance and its data (except vendored instance)
   */
  public async removeInstance(instance: Instance): Promise<OperationResult> {
    if (instance.kind === "vendored") {
      diag(
        "instance",
        `removeInstance: cannot remove vendored instance: ${instance.displayName}`
      )
      return {
        success: false,
        message: "Built-in instances cannot be removed",
      }
    }

    diag("instance", `removeInstance: ${instance.displayName}`)

    if (this.beforeRemove) {
      const shouldContinue = await this.beforeRemove(instance)
      if (!shouldContinue) {
        diag("instance", "removeInstance: cancelled by beforeRemove hook")
        return {
          success: false,
          message: "Removal cancelled by beforeRemove hook",
        }
      }
    }

    const result = await this.removeInstanceTE(instance)()

    const operationResult = pipe(
      result,
      E.fold(
        (error): OperationResult => {
          diag("instance", `removeInstance failed: ${error}`)
          if (this.onRemoveError) {
            this.onRemoveError(error, instance)
          }
          return { success: false, message: error }
        },
        (operationResult) => {
          diag("instance", "removeInstance successful")
          return operationResult
        }
      )
    )

    if (operationResult.success && this.afterRemove) {
      await this.afterRemove(instance)
    }

    return operationResult
  }

  /**
   * Disconnects from current instance
   */
  public async disconnect(): Promise<OperationResult> {
    diag("instance", "disconnect")

    const currentInstance = this.getCurrentInstance()

    if (currentInstance && this.beforeDisconnect) {
      const shouldContinue = await this.beforeDisconnect(currentInstance)
      if (!shouldContinue) {
        diag("instance", "disconnect: cancelled by beforeDisconnect hook")
        return {
          success: false,
          message: "Disconnection cancelled by beforeDisconnect hook",
        }
      }
    }

    const result = await this.disconnectTE()()

    const operationResult = pipe(
      result,
      E.fold(
        (error): OperationResult => {
          diag("instance", `disconnect failed: ${error}`)
          return { success: false, message: error }
        },
        (operationResult) => {
          diag("instance", "disconnect successful")
          return operationResult
        }
      )
    )

    if (operationResult.success && this.afterDisconnect) {
      await this.afterDisconnect()
    }

    return operationResult
  }

  /**
   * Clears all cached instances
   */
  public async clearCache(): Promise<OperationResult> {
    diag("instance", "clearCache")

    const result = await this.clearCacheTE()()

    return pipe(
      result,
      E.fold(
        (error): OperationResult => {
          diag("instance", `clearCache failed: ${error}`)
          return { success: false, message: error }
        },
        (operationResult) => {
          diag("instance", "clearCache successful")
          return operationResult
        }
      )
    )
  }

  /**
   * Closes a window
   */
  public async closeWindow(windowLabel?: string): Promise<OperationResult> {
    diag("instance", `closeWindow: ${windowLabel || "current"}`)

    const result = await pipe(
      this.performCloseTE(windowLabel),
      TE.chain((response) =>
        this.validateCloseResponseTE(response, windowLabel)
      )
    )()

    return pipe(
      result,
      E.fold(
        (error): OperationResult => {
          diag("instance", `closeWindow failed: ${error}`)
          return { success: false, message: error }
        },
        (closeResponse): OperationResult => {
          diag("instance", "closeWindow successful")
          return {
            success: true,
            message: `Successfully closed window ${windowLabel || "current"}`,
            data: closeResponse,
          }
        }
      )
    )
  }

  /**
   * Normalizes a URL
   */
  public normalizeUrl(url: string): string | null {
    return pipe(
      this.normalizeUrlE(url),
      E.fold(
        () => null,
        (normalized) => normalized
      )
    )
  }

  /**
   * Validates if a server URL is reachable and compatible
   */
  public async validateServerUrl(url: string): Promise<{
    valid: boolean
    version?: string
    instanceKind?: InstanceKind
    error?: string
  }> {
    diag("instance", `validateServerUrl: ${url}`)

    const normalizedUrl = this.normalizeUrl(url)
    if (!normalizedUrl) {
      return {
        valid: false,
        error: "Invalid URL format",
      }
    }

    try {
      const result = await this.performDownloadTE(normalizedUrl)()

      return pipe(
        result,
        E.fold(
          (
            error
          ): {
            valid: boolean
            version?: string
            instanceKind?: InstanceKind
            error?: string
          } => ({
            valid: false,
            error,
          }),
          (
            response
          ): {
            valid: boolean
            version?: string
            instanceKind?: InstanceKind
            error?: string
          } => {
            if (response.success) {
              diag("instance", "validateServerUrl: valid")
              return {
                valid: true,
                version: response.version,
                instanceKind: "on-prem" as InstanceKind,
              }
            } else {
              return {
                valid: false,
                error: "Server validation failed",
              }
            }
          }
        )
      )
    } catch (error) {
      diag("instance", `validateServerUrl failed: ${error}`)
      return {
        valid: false,
        error: `Validation failed: ${error}`,
      }
    }
  }

  public beforeConnect?: (
    serverUrl: string,
    instanceKind: InstanceKind,
    displayName?: string
  ) => Promise<boolean>

  public afterConnect?: (instance: Instance) => Promise<void>

  public beforeDisconnect?: (instance: Instance) => Promise<boolean>

  public afterDisconnect?: () => Promise<void>

  public beforeRemove?: (instance: Instance) => Promise<boolean>

  public afterRemove?: (instance: Instance) => Promise<void>

  public onConnectionError?: (error: string, target: string) => void

  public onDownloadError?: (error: string, serverUrl: string) => void

  public onLoadError?: (error: string, instance: Instance) => void

  public onRemoveError?: (error: string, instance: Instance) => void

  private getDefaultRecentInstances(): RecentInstances {
    return []
  }

  private getDefaultConnectionState(): ConnectionState {
    return { status: "idle" }
  }

  private loadRecentInstancesTE(): TE.TaskEither<string, RecentInstances> {
    return pipe(
      TE.tryCatch(
        async () => {
          const result = await Store.get<RecentInstances>(
            STORE_NAMESPACE,
            "recentInstances"
          )
          return result
        },
        (error) => `Failed to load recent instances: ${error}`
      ),
      TE.map(
        E.fold(
          () => this.getDefaultRecentInstances(),
          (instances) =>
            pipe(
              O.fromNullable(instances),
              O.fold(
                () => this.getDefaultRecentInstances(),
                (instances) =>
                  pipe(
                    instances,
                    sortByLastUsedDesc,
                    this.config?.maxRecentInstances
                      ? A.takeLeft(this.config.maxRecentInstances)
                      : (x) => x
                  )
              )
            )
        )
      )
    )
  }

  private loadConnectionStateTE(): TE.TaskEither<string, ConnectionState> {
    return pipe(
      TE.tryCatch(
        () => Store.get<ConnectionState>(STORE_NAMESPACE, "connectionState"),
        (error) => `Failed to load connection state: ${error}`
      ),
      TE.map(
        E.fold(
          () => this.getDefaultConnectionState(),
          (state) =>
            pipe(
              O.fromNullable(state),
              O.getOrElse(() => this.getDefaultConnectionState())
            )
        )
      )
    )
  }

  private initializeServiceTE(): TE.TaskEither<
    string,
    {
      instances: RecentInstances
      state: ConnectionState
    }
  > {
    return pipe(
      pipe(
        TE.tryCatch(
          () => Store.init(),
          (error) => `Store initialization failed: ${error}`
        ),
        TE.chainEitherK(E.mapLeft(() => "Failed to initialize store"))
      ),
      TE.chain(() =>
        pipe(
          TE.Do,
          TE.bind("instances", () => this.loadRecentInstancesTE()),
          TE.bind("state", () => this.loadConnectionStateTE())
        )
      )
    )
  }

  private updateConnectionState(state: ConnectionState): void {
    this.connectionState$.next(state)
    this.emit(state)
  }

  private updateRecentInstances(instances: RecentInstances): void {
    this.recentInstances$.next(instances)
  }

  override async onServiceInit(): Promise<void> {
    diag("instance", "InstanceService.onServiceInit() starting")
    diag("instance", "location.href:", window.location.href)

    // track when this webview is being unloaded (closed or navigated away).
    // uses synchronous XMLHttpRequest to write the log before the page dies,
    // since async writes (fetch, Tauri invoke) may not complete in time.
    // this is a debugging aid to detect unexpected webview replacements.
    window.addEventListener("beforeunload", () => {
      if (!(window as any).__TAURI_INTERNALS__) return

      const orgParam = new URLSearchParams(window.location.search).get("org")
      const tag = orgParam ? `org(${orgParam})` : "vendored"
      const label = getCurrentWebviewWindow().label
      // synchronous Tauri IPC isn't available, so we use the raw internals
      // this may not always complete before the page is torn down
      ;(window as any).__TAURI_INTERNALS__
        .invoke("append_log", {
          filename: "io.hoppscotch.desktop.diag.log",
          content: `[${new Date().toISOString()}] [BEFOREUNLOAD] tag=${tag}, label=${label}, href=${window.location.href}\n`,
        })
        .catch(() => {})
    })

    diag("instance", "initializing service")

    await pipe(
      this.initializeServiceTE(),
      TE.chain(({ instances }) => {
        this.updateRecentInstances(instances)

        return pipe(
          TE.of({ instances }),
          TE.chainFirst(() => this.initializeVendoredInstanceTE()),
          TE.chainFirst(() => this.detectCurrentInstanceFromHostname()),
          TE.map(() => {
            diag(
              "instance",
              `service initialized with ${this.recentInstances$.value.length} recent instances`
            )
          })
        )
      }),
      TE.fold(
        (error) => async () => {
          console.error("Service initialization failed:", error)
          this.updateConnectionState({
            status: "error",
            target: "store",
            message: error,
          })
        },
        () => async () => {
          diag("instance", "service initialization complete")
        }
      )
    )()
  }

  /**
   * Detects what instance the current webview is running by examining its hostname.
   * This is the source of truth for which instance we're "connected" to since each
   * webview runs as a separate instance with its own hostname encoded in the URL.
   */
  private detectCurrentInstanceFromHostname(): TE.TaskEither<string, void> {
    // With the query-param approach, all webviews share hostname "hoppscotch".
    // Org webviews get their context from the ?org= query param, which is
    // preserved across Vue Router navigations by a beforeEach guard and
    // survives full-page reloads because Tauri sets it on the initial URL.
    const orgHost = new URLSearchParams(window.location.search).get("org")
    const hostname = orgHost ?? window.location.hostname
    diag("instance", "detectCurrentInstanceFromHostname:")
    diag("instance", "  query ?org:", orgHost ?? "(none)")
    diag("instance", "  hostname:", hostname)
    diag("instance", "  window.location.hostname:", window.location.hostname)

    // vendored instance uses "hoppscotch" as hostname and has no org context
    if (hostname === "hoppscotch" && !orgHost) {
      const vendoredInstance = this.recentInstances$.value.find(
        (i) => i.kind === "vendored"
      )
      if (vendoredInstance) {
        diag("instance", "detectCurrentInstanceFromHostname: vendored instance")

        // guard: if the unified store already has a cloud-org connection,
        // do not overwrite it with vendored. this prevents a race after
        // the global hopp_auth://token event causes BOTH the vendored and
        // the org webview to reload. without this guard the vendored
        // webview's reload would clobber the org connection state and the
        // main window would resume vendored on next restart.
        return pipe(
          this.loadConnectionStateTE(),
          TE.chain((currentState) => {
            if (
              currentState.status === "connected" &&
              currentState.instance &&
              (currentState.instance.kind === "cloud-org" ||
                currentState.instance.kind === "cloud")
            ) {
              diag(
                "instance",
                `detectCurrentInstanceFromHostname: skipping vendored write, store already has ${currentState.instance.kind} connection (${currentState.instance.displayName})`
              )
              // still update the in-memory state so this webview knows it's
              // vendored, but don't persist to disk
              this.updateConnectionState({
                status: "connected",
                instance: vendoredInstance,
              })
              return TE.of(undefined)
            }

            // no org connection in the store, safe to write vendored
            return pipe(
              this.setConnectionState({
                status: "connected",
                instance: vendoredInstance,
              }),
              TE.map(() => undefined)
            )
          })
        )
      }
      return TE.of(undefined)
    }

    // cloud and cloud-org instances use hostnames like "hoppscotch_io" or
    // "acme_hoppscotch_io". convert underscores back to dots to get the
    // actual domain.
    const normalizedHostname = hostname.replace(/_/g, ".")
    diag("instance", `Normalized hostname: ${normalizedHostname}`)

    // check if this is a cloud hostname (hoppscotch.io or *.hoppscotch.io)
    const isCloudHostname =
      normalizedHostname.includes("hoppscotch.io") ||
      normalizedHostname.includes("hoppscotch.com")

    if (isCloudHostname) {
      const parts = normalizedHostname.split(".")
      // "hoppscotch.io" has 2 parts (default cloud), "acme.hoppscotch.io"
      // has 3+ parts (cloud-org with org subdomain)
      const isDefaultCloud = parts.length <= 2

      const instance: Instance = isDefaultCloud
        ? {
            kind: "cloud" as const,
            serverUrl: normalizedHostname,
            displayName: "Hoppscotch Cloud",
            version: VENDORED_INSTANCE_CONFIG.version,
            lastUsed: new Date().toISOString(),
            bundleName: VENDORED_INSTANCE_CONFIG.bundleName,
          }
        : {
            kind: "cloud-org" as const,
            serverUrl: normalizedHostname,
            displayName: `Cloud (${parts[0]})`,
            version: VENDORED_INSTANCE_CONFIG.version,
            lastUsed: new Date().toISOString(),
            bundleName: VENDORED_INSTANCE_CONFIG.bundleName,
          }

      diag(
        "instance",
        `Detected ${instance.kind} instance: ${instance.displayName}`
      )

      // persist to store so the main window can resume this instance on
      // restart. without persisting for cloud-org instances,
      // the main window would always load vendored on restart because the
      // vendored window's setConnectionState call during the org switch may
      // not complete before the vendored window is closed.
      return pipe(
        this.setConnectionState({
          status: "connected",
          instance,
        }),
        TE.map(() => undefined)
      )
    }

    // For other hostnames (on-prem instances), try to find in recent instances
    const matchingInstance = this.recentInstances$.value.find(
      (i) =>
        i.serverUrl.includes(hostname) ||
        hostname.includes(i.serverUrl.replace(/https?:\/\//, "").split("/")[0])
    )

    if (matchingInstance) {
      diag(
        "instance",
        `Detected instance from recent: ${matchingInstance.displayName}`
      )
      return pipe(
        this.setConnectionState({
          status: "connected",
          instance: matchingInstance,
        }),
        TE.map(() => undefined)
      )
    } else {
      diag("instance", `No matching instance found for hostname: ${hostname}`)
      // Default to idle state
      return pipe(
        this.setConnectionState({ status: "idle" }),
        TE.map(() => undefined)
      )
    }
  }

  private initializeVendoredInstanceTE(): TE.TaskEither<string, void> {
    diag("instance", "Checking for vendored instance")

    return pipe(
      TE.of(this.recentInstances$.value),
      TE.chain((currentInstances) => {
        const hasVendored = currentInstances.some((i) => i.kind === "vendored")

        if (hasVendored) {
          diag("instance", "Vendored instance already exists, skipping")
          return TE.of(undefined)
        } else {
          diag(
            "instance",
            "Vendored instance missing, adding to recent instances"
          )
          return pipe(
            TE.of(VENDORED_INSTANCE_CONFIG),
            TE.chainFirst((instance) => this.addToRecentInstancesTE(instance)),
            TE.map(() => undefined)
          )
        }
      })
    )
  }

  private setConnectionState(
    newState: ConnectionState
  ): TE.TaskEither<string, ConnectionState> {
    return pipe(
      TE.tryCatch(
        () => Store.set(STORE_NAMESPACE, "connectionState", newState),
        (error) => `Failed to save connection state: ${error}`
      ),
      TE.map((): ConnectionState => {
        this.updateConnectionState(newState)
        return newState
      })
    )
  }

  // restores connectionState$ synchronously after a failed transition.
  // updateConnectionState fires immediately so subscribers see the rollback
  // without waiting on the store. the Store.set persist is best-effort
  // because a stuck-UI fix shouldn't itself depend on a store-write
  // succeeding, and either branch logs so the failure is visible
  private restoreConnectionState(prevState: ConnectionState): void {
    this.updateConnectionState(prevState)
    void Store.set(STORE_NAMESPACE, "connectionState", prevState)
      .then((res) => {
        if (E.isLeft(res)) {
          diag("instance", `restoreConnectionState persist failed: ${res.left}`)
        }
      })
      .catch((e) => {
        diag("instance", `restoreConnectionState persist threw: ${e}`)
      })
  }

  private setRecentInstances(
    newInstances: RecentInstances
  ): TE.TaskEither<string, RecentInstances> {
    const limitedInstances = this.config?.maxRecentInstances
      ? pipe(newInstances, A.takeLeft(this.config.maxRecentInstances))
      : newInstances

    return pipe(
      TE.tryCatch(
        async () => {
          const result = await Store.set(
            STORE_NAMESPACE,
            "recentInstances",
            limitedInstances
          )
          return result
        },
        (error) => `Failed to save recent instances: ${error}`
      ),
      TE.map((): RecentInstances => {
        this.updateRecentInstances(limitedInstances)
        return limitedInstances
      })
    )
  }

  private normalizeUrlE(url: string): E.Either<string, string> {
    return pipe(
      E.tryCatch(
        () => {
          const parsed = new URL(url.includes("://") ? url : `http://${url}`)
          return `${parsed.protocol}//${parsed.host}/desktop-app-server`
        },
        (error) => `Failed to normalize URL: ${error}`
      )
    )
  }

  private setConnectingStateTE(
    target: string
  ): TE.TaskEither<string, ConnectionState> {
    diag("instance", `Setting connecting state for: ${target}`)
    return this.setConnectionState({ status: "connecting", target })
  }

  private performDownloadTE(
    serverUrl: string
  ): TE.TaskEither<string, DownloadResponse> {
    diag("instance", `Downloading from: ${serverUrl}`)

    const timeout = this.config?.connectionTimeout || 30000

    return TE.tryCatch(
      () =>
        Promise.race([
          download({ serverUrl }),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error(`Connection timeout after ${timeout}ms`)),
              timeout
            )
          ),
        ]),
      (error) => `Failed to download instance: ${error}`
    )
  }

  private validateDownloadResponseTE(
    response: DownloadResponse,
    serverUrl: string
  ): TE.TaskEither<string, DownloadResponse> {
    return response.success
      ? TE.right(response)
      : TE.left(`Download failed for ${serverUrl}`)
  }

  private createInstanceFromDownload(
    response: DownloadResponse,
    instanceKind: InstanceKind,
    displayName?: string
  ): Instance {
    return {
      kind: instanceKind,
      serverUrl: response.serverUrl,
      displayName: displayName || response.serverUrl,
      version: response.version,
      lastUsed: new Date().toISOString(),
      bundleName: response.bundleName,
    }
  }

  private downloadInstanceTE(
    serverUrl: string,
    instanceKind: InstanceKind,
    displayName?: string
  ): TE.TaskEither<string, Instance> {
    return pipe(
      this.normalizeUrlE(serverUrl),
      TE.fromEither,
      TE.chain((normalizedUrl) =>
        pipe(
          this.setConnectingStateTE(normalizedUrl),
          TE.chain(() => this.performDownloadTE(normalizedUrl)),
          TE.chain((response) =>
            this.validateDownloadResponseTE(response, normalizedUrl)
          ),
          TE.map((response) =>
            this.createInstanceFromDownload(response, instanceKind, displayName)
          )
        )
      )
    )
  }

  private getBundleNameTE(instance: Instance): TE.TaskEither<string, string> {
    return pipe(
      O.fromNullable(instance.bundleName),
      TE.fromOption(() => `Instance ${instance.displayName} has no bundle name`)
    )
  }

  private buildLoadOptions(
    instance: Instance,
    options?: Partial<LoadOptions>
  ): LoadOptions {
    const defaultWindowOptions = this.config?.defaultWindowOptions || {
      title: "Hoppscotch",
      width: 1200,
      height: 800,
      resizable: true,
    }

    // cloud and cloud-org instances pass serverUrl as host so
    // window.location.hostname reflects the correct context. cloud-org gets
    // something like app://acme_hoppscotch_io/, default cloud gets
    // app://hoppscotch_io/. without this, default cloud would load at
    // app://hoppscotch/ (same as vendored) and `detectCurrentInstanceFromHostname`
    // couldn't tell them apart.
    const host =
      instance.kind === "cloud-org" || instance.kind === "cloud"
        ? instance.serverUrl
        : options?.host

    return {
      bundleName: instance.bundleName!,
      host,
      inline: options?.inline ?? true,
      window: options?.window ?? defaultWindowOptions,
    }
  }

  private performLoadTE(
    loadOptions: LoadOptions
  ): TE.TaskEither<string, LoadResponse> {
    diag(
      "instance",
      `performLoadTE: bundle=${loadOptions.bundleName}, host=${loadOptions.host ?? "(none)"}, title=${loadOptions.window?.title ?? "(default)"}`
    )

    return TE.tryCatch(
      async () => {
        const result = await load(loadOptions)
        diag(
          "instance",
          `performLoadTE result: success=${result.success}, label=${result.windowLabel}`
        )
        return result
      },
      (error) => {
        diag("instance", `performLoadTE error: ${error}`)
        return `Failed to load instance: ${error}`
      }
    )
  }

  private validateLoadResponseTE(
    response: LoadResponse,
    displayName: string
  ): TE.TaskEither<string, LoadResponse> {
    return response.success
      ? TE.right(response)
      : TE.left(`Failed to load instance ${displayName}`)
  }

  private updateInstanceStateTE(
    instance: Instance
  ): TE.TaskEither<string, ConnectionState> {
    diag("instance", "Updating instance state to connected")
    return this.setConnectionState({ status: "connected", instance })
  }

  private performCloseTE(
    windowLabel?: string
  ): TE.TaskEither<string, CloseResponse> {
    diag("instance", `Closing window: ${windowLabel || "current"}`)

    return TE.tryCatch(
      async () => {
        const currentWindow = getCurrentWebviewWindow()
        const labelToClose = windowLabel || currentWindow.label

        diag(
          "instance",
          `performCloseTE: labelToClose=${labelToClose}, currentWindow=${currentWindow.label}`
        )

        if (labelToClose === "main") {
          diag("instance", "performCloseTE: refusing to close main window")
          throw new Error("Cannot close main window")
        }

        const closeResponse = await close({ windowLabel: labelToClose })
        diag(
          "instance",
          `performCloseTE result: success=${closeResponse.success}`
        )
        return closeResponse
      },
      (error) => {
        diag("instance", `performCloseTE error: ${error}`)
        return `Failed to close window: ${error}`
      }
    )
  }

  private validateCloseResponseTE(
    response: CloseResponse,
    windowLabel?: string
  ): TE.TaskEither<string, CloseResponse> {
    return response.success
      ? TE.right(response)
      : TE.left(`Failed to close window ${windowLabel || "current"}`)
  }

  private loadInstanceTE(
    instance: Instance,
    options?: Partial<LoadOptions>
  ): TE.TaskEither<string, LoadResponse> {
    diag(
      "instance",
      `loadInstanceTE: kind=${instance.kind}, displayName=${instance.displayName}, serverUrl=${instance.serverUrl}, host=${options?.host ?? "(none)"}`
    )
    // instance came from the recents list, where its version and
    // bundleName go stale once the server publishes a new bundle.
    // loadedInstance is the same Instance with those two fields
    // replaced by what download() returned from the on-prem
    // manifest. Every later step uses loadedInstance so the
    // connected state and the recents row both reference the
    // version loaded into the webview.
    let loadedInstance: Instance = instance
    return pipe(
      // Skips download for vendored, cloud, and cloud-org instances since they all use
      // the same embedded Hoppscotch bundle. vendored is the base version, cloud and
      // cloud-org are just different host contexts for the same bundle. Only on-prem
      // instances need downloading since they have their own unique bundles.
      instance.kind === "vendored" ||
        instance.kind === "cloud-org" ||
        instance.kind === "cloud"
        ? TE.of(undefined)
        : TE.tryCatch(
            async () => {
              diag(
                "instance",
                `Ensuring bundle is available for: ${instance.displayName}`
              )
              const dlResp = await download({ serverUrl: instance.serverUrl })
              loadedInstance = {
                ...instance,
                version: dlResp.version,
                bundleName: dlResp.bundleName,
              }
            },
            (error) => `Failed to ensure bundle is available: ${error}`
          ),
      TE.chain(() => this.getBundleNameTE(loadedInstance)),
      TE.map(() => this.buildLoadOptions(loadedInstance, options)),
      TE.chain((loadOptions) => this.performLoadTE(loadOptions)),
      TE.chain((response) =>
        this.validateLoadResponseTE(response, loadedInstance.displayName)
      ),
      TE.chainFirst(() => {
        diag(
          "instance",
          `loadInstanceTE: load validated, updating state to connected for ${loadedInstance.displayName}`
        )
        return this.updateInstanceStateTE(loadedInstance)
      }),
      TE.chainFirst(() => this.updateInstanceLastUsed(loadedInstance)),
      TE.chainFirst(() =>
        TE.fromTask(async () => {
          diag(
            "instance",
            `loadInstanceTE: about to close current window after successful load of ${instance.displayName}`
          )
          try {
            const closeResult = await this.performCloseTE()()
            if (E.isRight(closeResult)) {
              const validateResult = await this.validateCloseResponseTE(
                closeResult.right
              )()
              if (E.isRight(validateResult)) {
                diag(
                  "instance",
                  "loadInstanceTE: current window closed successfully"
                )
                diag("instance", "Window closed successfully after load")
              } else {
                diag(
                  "instance",
                  `loadInstanceTE: close validation failed: ${validateResult.left}`
                )
                diag(
                  "instance",
                  `Window close validation failed but load succeeded: ${validateResult.left}`
                )
              }
            } else {
              diag(
                "instance",
                `loadInstanceTE: close failed: ${closeResult.left}`
              )
              diag(
                "instance",
                `Window close failed but load succeeded: ${closeResult.left}`
              )
            }
          } catch (error) {
            diag("instance", `loadInstanceTE: close error: ${error}`)
            diag("instance", `Window close error but load succeeded: ${error}`)
          }
          return undefined
        })
      )
    )
  }

  private performRemoveTE(
    bundleName: string,
    serverUrl: string
  ): TE.TaskEither<string, RemoveResponse> {
    diag("instance", `Removing bundle: ${bundleName}`)

    return TE.tryCatch(
      () => remove({ bundleName, serverUrl }),
      (error) => `Failed to remove instance: ${error}`
    )
  }

  private validateRemoveResponseTE(
    response: RemoveResponse,
    displayName: string
  ): TE.TaskEither<string, RemoveResponse> {
    return response.success
      ? TE.right(response)
      : TE.left(`Failed to remove instance ${displayName}`)
  }

  private removeFromRecentInstancesTE(
    serverUrl: string
  ): TE.TaskEither<string, RecentInstances> {
    return pipe(
      this.recentInstances$.value,
      A.filter((i) => {
        // Vendored instance must always stay in the list since it's the fallback when
        // everything else fails. Without vendored in the list, the app could get into a
        // state where no instances are available to load.
        if (i.kind === "vendored") {
          diag("instance", `Preserving vendored instance: ${i.displayName}`)
          return true
        }
        return i.serverUrl !== serverUrl
      }),
      (filtered) => this.setRecentInstances(filtered)
    )
  }

  private removeInstanceTE(
    instance: Instance
  ): TE.TaskEither<string, OperationResult> {
    return pipe(
      O.fromNullable(instance.bundleName),
      O.fold(
        () =>
          TE.left(`Instance ${instance.displayName} has no bundle to remove`),
        (bundleName) =>
          pipe(
            this.performRemoveTE(bundleName, instance.serverUrl),
            TE.chain((response) =>
              this.validateRemoveResponseTE(response, instance.displayName)
            ),
            TE.chainFirst(() =>
              this.removeFromRecentInstancesTE(instance.serverUrl)
            ),
            TE.map((): OperationResult => ({
              success: true,
              message: `Successfully removed ${instance.displayName}`,
            }))
          )
      )
    )
  }

  private clearCacheTE(): TE.TaskEither<string, OperationResult> {
    return pipe(
      TE.tryCatch(
        () => clear(),
        (error) => `Failed to clear cache: ${error}`
      ),
      TE.chainFirst(() => {
        const vendoredInstance = this.recentInstances$.value.find(
          (i) => i.kind === "vendored"
        )
        const instancesToKeep = vendoredInstance ? [vendoredInstance] : []
        return this.setRecentInstances(instancesToKeep)
      }),
      TE.map((): OperationResult => ({
        success: true,
        message: "Cache cleared successfully",
      }))
    )
  }

  private createOrGetInstanceTE(
    serverUrl: string,
    instanceKind: InstanceKind,
    displayName?: string
  ): TE.TaskEither<string, Instance> {
    return pipe(
      this.findInstanceByUrl(serverUrl),
      O.fold(
        () => {
          diag("instance", `Instance not found, downloading new: ${serverUrl}`)
          return pipe(
            this.downloadInstanceTE(serverUrl, instanceKind, displayName),
            TE.chainFirst((newInstance) =>
              this.addToRecentInstancesTE(newInstance)
            )
          )
        },
        (existing) => {
          diag("instance", `Using existing instance: ${existing.displayName}`)
          return TE.right(existing)
        }
      )
    )
  }

  private addToRecentInstancesTE(
    instance: Instance
  ): TE.TaskEither<string, RecentInstances> {
    diag("instance", `Adding to recent instances: ${instance.displayName}`)

    return pipe(
      this.recentInstances$.value,
      A.prepend(instance),
      sortByLastUsedDesc,
      (updated) => this.setRecentInstances(updated)
    )
  }

  private connectToInstanceTE(
    serverUrl: string,
    instanceKind: InstanceKind,
    displayName?: string,
    options?: Partial<LoadOptions>
  ): TE.TaskEither<string, OperationResult> {
    return pipe(
      this.normalizeUrlE(serverUrl),
      TE.fromEither,
      TE.chain((normalizedUrl) =>
        pipe(
          this.createOrGetInstanceTE(normalizedUrl, instanceKind, displayName),
          TE.chain((instance) => this.loadInstanceTE(instance, options)),
          TE.map((): OperationResult => ({
            success: true,
            message: `Successfully connected to ${displayName || serverUrl}`,
          }))
        )
      )
    )
  }

  private findInstanceByUrl(serverUrl: string): O.Option<Instance> {
    const extractHost = (url: string): string =>
      url.startsWith("app://")
        ? url.split("/")[2]
        : pipe(
            E.tryCatch(
              () => new URL(url).host,
              () => url
            ),
            E.getOrElse((fallback) => fallback)
          )

    const targetHost = extractHost(serverUrl)

    diag(
      "instance",
      `Looking up ${targetHost} in ${JSON.stringify(this.recentInstances$.value, null, 2)}`
    )

    return pipe(
      this.recentInstances$.value,
      A.findFirst((instance) => extractHost(instance.serverUrl) === targetHost)
    )
  }

  private updateInstanceLastUsed(
    instance: Instance
  ): TE.TaskEither<string, void> {
    diag("instance", `Updating last used for: ${instance.displayName}`)

    return pipe(
      this.recentInstances$.value,
      A.map((i) =>
        i.serverUrl === instance.serverUrl
          ? {
              ...i,
              version: instance.version,
              bundleName: instance.bundleName,
              lastUsed: new Date().toISOString(),
            }
          : i
      ),
      sortByLastUsedDesc,
      (updated) => this.setRecentInstances(updated),
      TE.map(() => undefined)
    )
  }

  private disconnectTE(): TE.TaskEither<string, OperationResult> {
    return pipe(
      this.performCloseTE(),
      TE.chain((response) => this.validateCloseResponseTE(response)),
      TE.map((): OperationResult => ({
        success: true,
        message: "Disconnected successfully",
      })),
      TE.orElse((error) => {
        if (error.includes("Cannot close main window")) {
          diag("instance", "Disconnected (main window remains open)")
          return TE.right({
            success: true,
            message: "Disconnected (main window remains open)",
          })
        }
        return TE.left(error)
      })
    )
  }
}

const desktopInstanceService = getService(DesktopInstanceService)

export const def: InstancePlatformDef = desktopInstanceService
